// Port of the sortCards()/titleOrCreatedOrder()/manualOrder() helpers that
// upstream keeps inside webapp/src/store/cards.ts.

import {Board} from '../types/board'
import {BoardView, ISortOption} from '../types/boardView'
import {Card} from '../types/card'
import {IUser} from '../types/user'
import {Constants} from '../common/constants'
import {sortValueOf} from './properties'

type UsersById = Record<string, IUser>

function titleOrCreatedOrder(cardA: Card, cardB: Card): number {
    const titleA = cardA.title.trim()
    const titleB = cardB.title.trim()

    if (titleA === titleB) {
        return cardA.createAt - cardB.createAt
    }
    if (!titleA) {
        return 1
    }
    if (!titleB) {
        return -1
    }
    return titleA.localeCompare(titleB)
}

function manualOrder(activeView: BoardView, cardA: Card, cardB: Card): number {
    const indexA = activeView.fields.cardOrder.indexOf(cardA.id)
    const indexB = activeView.fields.cardOrder.indexOf(cardB.id)

    if (indexA < 0 && indexB < 0) {
        return titleOrCreatedOrder(cardA, cardB)
    }
    if (indexA < 0 && indexB >= 0) {
        // if cardA's order is not defined, put it at the end
        return 1
    }
    return indexA - indexB
}

function compareValues(aValue: string | number, bValue: string | number, type: string): number {
    if (type === 'number' || type === 'date' || type === 'createdTime' || type === 'updatedTime') {
        if (aValue === '' && bValue !== '') {
            return 1
        }
        if (bValue === '' && aValue !== '') {
            return -1
        }
        if (aValue === '' && bValue === '') {
            return 0
        }
        return Number(aValue) - Number(bValue)
    }

    const a = String(aValue)
    const b = String(bValue)
    if (a === b) {
        return 0
    }
    if (!a) {
        return 1
    }
    if (!b) {
        return -1
    }
    return a.localeCompare(b)
}

/**
 * Multi-key sort. Upstream always applies the sort as a stable refinement over
 * the existing order and keeps untitled cards at the bottom.
 */
export function sortCards(
    cards: Card[],
    board: Board,
    activeView: BoardView | undefined,
    usersById: UsersById = {},
    lastCommentTimeByCard: Record<string, number> = {},
): Card[] {
    if (!activeView) {
        return cards
    }
    const sortOptions: ISortOption[] = activeView.fields.sortOptions || []

    if (sortOptions.length < 1) {
        return cards.slice().sort((a, b) => manualOrder(activeView, a, b))
    }

    let sorted = cards.slice()
    sortOptions.forEach((sortOption) => {
        if (sortOption.propertyId === Constants.titleColumnId) {
            sorted = sorted.sort((a, b) => {
                const result = titleOrCreatedOrder(a, b)
                return sortOption.reversed ? -result : result
            })
            return
        }

        const template = board.cardProperties.find((o) => o.id === sortOption.propertyId)
        if (!template) {
            console.warn(`[sortCards] missing template for property id: ${sortOption.propertyId}`)
            return
        }

        sorted = sorted.sort((a, b) => {
            // cards with no value in this property always sink to the bottom
            const aHas = hasValue(a, template.id, usersById)
            const bHas = hasValue(b, template.id, usersById)
            if (!aHas && bHas) {
                return 1
            }
            if (aHas && !bHas) {
                return -1
            }
            if (!aHas && !bHas) {
                return titleOrCreatedOrder(a, b)
            }

            let result: number
            if (template.type === 'updatedTime') {
                const aUpdateAt = Math.max(a.updateAt, lastCommentTimeByCard[a.id] || 0)
                const bUpdateAt = Math.max(b.updateAt, lastCommentTimeByCard[b.id] || 0)
                result = aUpdateAt - bUpdateAt
            } else {
                result = compareValues(
                    sortValueOf(a, template, usersById),
                    sortValueOf(b, template, usersById),
                    template.type,
                )
            }
            if (result === 0) {
                result = titleOrCreatedOrder(a, b)
            }
            return sortOption.reversed ? -result : result
        })
    })

    return sorted
}

function hasValue(card: Card, propertyId: string, usersById: UsersById): boolean {
    if (propertyId === 'createdBy') {
        return Boolean(usersById[card.createdBy])
    }
    if (propertyId === 'updatedBy') {
        return Boolean(usersById[card.modifiedBy])
    }
    const raw = card.fields.properties[propertyId]
    if (Array.isArray(raw)) {
        return raw.length > 0
    }
    return Boolean(raw) && raw !== 'false'
}

export function cardsOfBoard(cards: Record<string, Card>, boardId: string, includeTemplates = false): Card[] {
    return Object.values(cards).
        filter((c) => c.boardId === boardId && c.deleteAt === 0 && (includeTemplates || !c.fields.isTemplate))
}

export {titleOrCreatedOrder, manualOrder}
